/**
 * The type-specific half of each behavior's settings page, and the words the
 * template page uses for that behavior. One entry per AgentBehaviorKind, so a
 * new behavior cannot ship without its panel (the Record type requires it).
 */

import type { ComponentType } from 'react';
import type { AgentBehaviorKind } from '../../../lib/ai/agentDefinitions';
import type { BehaviorSettingsPanelProps } from './types';
import { EstimateValueSettings } from './EstimateValueSettings';
import { ClassifySettings } from './ClassifySettings';
import { ClusterSettings } from './ClusterSettings';
import { RelationshipSettings } from './RelationshipSettings';
import { DraftItemsSettings } from './DraftItemsSettings';
import { FindingsSettings } from './FindingsSettings';
import { NAMED_ACCEPTANCE, VALUE_ACCEPTANCE } from './acceptanceWords';

export interface BehaviorSettingsCopy {
  Panel: ComponentType<BehaviorSettingsPanelProps>;
  /** Run panel: the "everything missing" scope. */
  allEmptyLabel: string;
  allEmptyDescription: string;
  /** The item_created switch. */
  autoRunLabel: string;
  autoRunDescription: string;
  /** What a run does, one sentence, with the cap filled in. */
  runSummary: (cap: number) => string;
  docsDescription: string;
  docsPlaceholder: string;
  learnedRulePlaceholder: string;
  /** The "From one item" switch: where a person asks it about one item. */
  manualSingleHelp: string;
  /** The "Show it on the item" switch: where its proposals appear. */
  surfaceOnObjectHelp: string;
  /** Where a run's proposals appear, for the Run now line. */
  whereProposalsShow: string;
  /** What it never does, in one line under "How it works". */
  neverWrites: string;
}

export const BEHAVIOR_SETTINGS: Record<AgentBehaviorKind, BehaviorSettingsCopy> = {
  estimate_value: {
    Panel: EstimateValueSettings,
    allEmptyLabel: 'All items missing a value',
    allEmptyDescription: 'Active items with no accepted value on an allowed attribute',
    autoRunLabel: 'Estimate new items automatically',
    autoRunDescription: 'When an item is created, propose for each allowed attribute right away. A thin item still gets an abstain, not a guess.',
    runSummary: (cap) => `Proposes for every active item that lacks an accepted value on each allowed attribute, up to ${cap} items.`,
    docsDescription: 'A sizing guide, examples of what each point means here, anything the agent should read before estimating.',
    docsPlaceholder: 'What a 1, 3 and 5 mean here; examples of sized work; anything else the estimator should know.',
    learnedRulePlaceholder: 'Write a rule yourself, e.g. Anything touching billing is at least Medium effort here.',
    manualSingleHelp: 'An Estimate button on each allowed value of an item.',
    surfaceOnObjectHelp: 'Its Estimate button and its proposals appear in the item\'s Values section. Off: only on its report page.',
    whereProposalsShow: 'Proposals appear on each item\'s Values section and on its report page.',
    neverWrites: `It never writes a value, a capacity unit, or any field on its own. ${VALUE_ACCEPTANCE}`,
  },
  classify: {
    Panel: ClassifySettings,
    allEmptyLabel: 'All unclassified items',
    allEmptyDescription: 'Active items with no category and altitude yet',
    autoRunLabel: 'Classify new items automatically',
    autoRunDescription: 'When an item is created, propose its category and altitude right away, on the server.',
    runSummary: (cap) => `Proposes a category and altitude for every active item not yet classified or proposed, up to ${cap} items.`,
    docsDescription: 'Category examples, altitude guidance, pairs of items and the altitude they belong at — anything the agent should read before classifying.',
    docsPlaceholder: 'What a problem versus an opportunity looks like here; examples of altitude 1 and altitude 5 work.',
    learnedRulePlaceholder: 'Write a rule yourself, e.g. Anything regulatory is a commitment.',
    manualSingleHelp: '"Suggest a category and altitude" on each item, and "Ask" when an item has none.',
    surfaceOnObjectHelp: 'Its proposals appear on the item, where a person accepts all, part, or none. Off: only on its report page.',
    whereProposalsShow: 'Proposals appear on each item and on its report page.',
    neverWrites: `It never sets a category or altitude itself. ${NAMED_ACCEPTANCE}`,
  },
  detect_cluster: {
    Panel: ClusterSettings,
    allEmptyLabel: 'All active items',
    allEmptyDescription: 'Every active item, compared 40 at a time',
    autoRunLabel: 'Check new items automatically',
    autoRunDescription: 'When an item is created, compare it with the most similar items in the workspace, on the server.',
    runSummary: (cap) => `Compares up to ${cap} active items, 40 at a time, and proposes groups of duplicates and variants.`,
    docsDescription: 'What counts as the same work here — product areas, words your teams use for the same thing, past merges.',
    docsPlaceholder: 'For example: "SSO" and "single sign-on" are the same; requests from different customers for the same feature are duplicates.',
    learnedRulePlaceholder: 'Write a rule yourself, e.g. Bugs in different products are never duplicates.',
    manualSingleHelp: 'Not offered for one item: it compares items with each other.',
    surfaceOnObjectHelp: 'Its proposals appear where possible duplicates already show. Off: only on its report page.',
    whereProposalsShow: 'Proposals appear on its report page and in each pool\'s structural checks.',
    neverWrites: `It never merges, links or closes an item itself. ${NAMED_ACCEPTANCE}`,
  },
  propose_relationship: {
    Panel: RelationshipSettings,
    allEmptyLabel: 'All active items',
    allEmptyDescription: 'Every active item, compared 25 at a time',
    autoRunLabel: 'Check new items automatically',
    autoRunDescription: 'When an item is created, look for relationships between it and the most similar items, on the server.',
    runSummary: (cap) => `Compares up to ${cap} active items, 25 at a time, and proposes relationships between pairs.`,
    docsDescription: 'How work fits together here — product structure, program breakdowns, what usually blocks what.',
    docsPlaceholder: 'For example: platform work comes before the features built on it; each initiative breaks down into epics.',
    learnedRulePlaceholder: 'Write a rule yourself, e.g. Security reviews are a prerequisite for any launch.',
    manualSingleHelp: '"Find how it relates to other items" on each item: it answers in plain words and saves nothing.',
    surfaceOnObjectHelp: 'Its proposals appear in the relationships queue and on each pool\'s structural checks. Off: only on its report page.',
    whereProposalsShow: 'Proposals appear on its report page, in the relationships queue and in each pool\'s structural checks.',
    neverWrites: `It never creates a relationship itself. ${NAMED_ACCEPTANCE}`,
  },
  draft_items: {
    Panel: DraftItemsSettings,
    allEmptyLabel: '',
    allEmptyDescription: '',
    autoRunLabel: '',
    autoRunDescription: '',
    runSummary: () => '',
    docsDescription: 'How this workspace names and sizes work — so drafts come out in your words.',
    docsPlaceholder: 'For example: we call customer-facing changes "launches"; anything under a week is altitude 1.',
    learnedRulePlaceholder: '',
    manualSingleHelp: 'The Scribe page, for anyone who can create items.',
    surfaceOnObjectHelp: '',
    whereProposalsShow: '',
    neverWrites: 'It never creates an item itself: the person creates the drafts they keep.',
  },
  find_issues: {
    Panel: FindingsSettings,
    allEmptyLabel: 'The whole workspace',
    allEmptyDescription: 'It checks the whole workspace record each run',
    autoRunLabel: '',
    autoRunDescription: '',
    runSummary: () => 'Checks the whole workspace record and replaces its open findings with what it finds now; reviewed findings are kept.',
    docsDescription: '',
    docsPlaceholder: '',
    learnedRulePlaceholder: '',
    manualSingleHelp: 'Not offered for one item: it checks the whole workspace.',
    surfaceOnObjectHelp: 'Its findings appear on the items they name and on its report page. Off: only on its report page.',
    whereProposalsShow: 'Findings appear on its report page.',
    neverWrites: 'It never changes a ranking, an objective, a commitment or a condition: a finding is advice for a person to accept or dismiss.',
  },
};

export type { BehaviorSettingsPanelProps } from './types';
